import { useCallback, useMemo, useState } from 'react';
import { toast } from '@/components';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { executeContinue } from '@/hooks/useQuery';
import { useSortFromUrl } from '@/hooks/useSortFromUrl';
import { useQueryResultsStore } from '@/stores/query-results.store';
import { sortEvents, SortOption } from '@/utils/query-filters';
import type { ScoredEvent } from '@/types/event';

interface QueryResultsPagination {
    events: ScoredEvent[];
    hasMore: boolean;
    isLoadingMore: boolean;
    loadMore: () => Promise<void>;
}

export function useQueryResultsPagination(): QueryResultsPagination {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const events = useQueryResultsStore((state) => state.events);
    const eventPageMap = useQueryResultsStore((state) => state.eventPageMap);
    const hasMore = useQueryResultsStore((state) => state.hasMore);
    const cursor = useQueryResultsStore((state) => state.cursor);
    const queryLoading = useQueryResultsStore((state) => state.queryLoading);
    const addEvent = useQueryResultsStore((state) => state.addEvent);
    const incrementPage = useQueryResultsStore((state) => state.incrementPage);
    const setHasMore = useQueryResultsStore((state) => state.setHasMore);
    const setCursor = useQueryResultsStore((state) => state.setCursor);
    const setAnalyzedQueries = useQueryResultsStore(
        (state) => state.setAnalyzedQueries,
    );
    const setLoadMoreSummary = useQueryResultsStore(
        (state) => state.setLoadMoreSummary,
    );

    const [sortOption] = useSortFromUrl(SortOption.CONFIDENCE);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const sortedEvents = useMemo(
        () => sortEvents(events, sortOption, eventPageMap),
        [events, sortOption, eventPageMap],
    );

    const loadMore = useCallback(async () => {
        if (!cursor || isLoadingMore || !hasMore || queryLoading) return;

        setIsLoadingMore(true);
        incrementPage();
        setLoadMoreSummary(null);

        try {
            const results = await executeContinue(cursor, 10, {
                onEventReceived: addEvent,
                onMeta: (meta) => {
                    setCursor(meta.cursor ?? null);
                    setHasMore(!!meta.cursor);
                    if (meta.summary) setLoadMoreSummary(meta.summary);
                },
                onAnalyzedQueries: setAnalyzedQueries,
            });

            if (results.length === 0) {
                setHasMore(false);
            }
        } catch {
            toast.error(t('page.loadMoreError'));
        } finally {
            setIsLoadingMore(false);
        }
    }, [
        cursor,
        isLoadingMore,
        hasMore,
        queryLoading,
        addEvent,
        incrementPage,
        setHasMore,
        setCursor,
        setLoadMoreSummary,
        setAnalyzedQueries,
        t,
    ]);

    return {
        events: sortedEvents,
        hasMore: hasMore && !queryLoading,
        isLoadingMore,
        loadMore,
    };
}
